const ecoSystemTypes = [
  "Tropical-subtropical lowland rainforests",
  "Tropical-subtropical dry forests and thickets",
  "Tropical-subtropical montane rainforests",
  "Tropical heath forests",
  "Boreal and temperate high montane forests and woodlands",
  "Deciduous temperate forests",
  "Oceanic cool temperate rainforests",
  "Warm temperate laurophyll forests",
  "Temperate pyric humid forests",
  "Temperate pyric sclerophyll forests and woodlands 49",
  "Seasonally dry tropical shrublands",
  "Seasonally dry temperate heaths and shrublands",
  "Cool temperate heathlands",
  "Rocky pavements or lava flows and screes",
  "Trophic savanna",
  "Pyric tussock savanna",
  "Hummock savanna",
  "Temperate woodland",
  "Temperate subhumid grasslands",
  "Semi-desert steppes",
  "Succulent or Thorny deserts and semi-deserts",
  "Sclerophyll hot deserts and semi-deserts",
  "Cool deserts and semi-deserts",
  "Hyper-arid deserts",
  "Ice sheets, glaciers and perennial snowfields",
  "Polar alpine rocky outcrops",
  "Polar tundra and deserts",
  "Temperate alpine grasslands and shrublands",
  "Tropical alpine grasslands and herbfields",
  "Tropical flooded forests and peat forests",
  "Subtropical-temperate forested wetlands",
  "Permanent marshes",
  "Seasonal floodplain marshes",
  "Episodic arid floodplains",
  "Boreal, temperate and montane peat bogs",
  "Boreal and temperate fens",
  "Coastal shrublands and grasslands",
  "Intertidal mangrove forests and shrublands",
  "Coastal saltmarshes and reedbeds",
  "Annual croplands",
  "Sown pastures and fields",
  "Plantations",
  "Home gardens",
  "Botanical Gardens",
  "Arboreturms",
  "Restorative ecosystems",
];

// IUCN Global Ecosystem Typology v2.0 (Keith et al., 2020) — Marine realm and the
// Subterranean-Marine biome are excluded. The two-level dropdown (main = Biome,
// sub = Functional Group) captures the off-chain IUCN sub-code for a land; the
// on-chain covenant records only the top-level T-band 1–8 (see CovenantBadge's
// IUCN_T_BAND_OPTIONS). Options are keyed on the IUCN code (e.g. "T1", "T1.1")
// rather than the display name because names contain commas and ecoSystemType is
// persisted as a comma-joined `main,sub` string — codes keep that split lossless.
export interface IucnFunctionalGroup {
  id: string;
  name: string;
}
export interface IucnBiome {
  id: string;
  name: string;
  realm: string;
  functionalGroups: IucnFunctionalGroup[];
}

export const IUCN_ECOSYSTEM_TYPOLOGY: IucnBiome[] = [
  {
    id: "T1",
    name: "Tropical-Subtropical Forests",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T1.1", name: "Tropical subtropical lowland rainforests" },
      { id: "T1.2", name: "Tropical subtropical dry forests and thickets" },
      { id: "T1.3", name: "Tropical-subtropical montane rainforests" },
      { id: "T1.4", name: "Tropical heath forests" },
    ],
  },
  {
    id: "T2",
    name: "Temperate-Boreal Forests and Woodlands",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T2.1", name: "Boreal and temperate high montane forests and woodlands" },
      { id: "T2.2", name: "Deciduous temperate forests" },
      { id: "T2.3", name: "Oceanic cool temperate rainforests" },
      { id: "T2.4", name: "Warm temperate laurophyll forests" },
      { id: "T2.5", name: "Temperate pyric humid forests" },
      { id: "T2.6", name: "Temperate pyric sclerophyll forests and woodlands" },
    ],
  },
  {
    id: "T3",
    name: "Shrublands and Shrubby Woodlands",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T3.1", name: "Seasonally dry tropical shrublands" },
      { id: "T3.2", name: "Seasonally dry temperate heaths and shrublands" },
      { id: "T3.3", name: "Cool temperate heathlands" },
      { id: "T3.4", name: "Rocky pavements, lava flows and screes" },
    ],
  },
  {
    id: "T4",
    name: "Savannas and Grasslands",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T4.1", name: "Trophic savannas" },
      { id: "T4.2", name: "Pyric tussock savannas" },
      { id: "T4.3", name: "Hummock savannas" },
      { id: "T4.4", name: "Temperate woodlands" },
      { id: "T4.5", name: "Temperate subhumid grasslands" },
    ],
  },
  {
    id: "T5",
    name: "Deserts and Semi-Deserts",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T5.1", name: "Semi-desert steppes" },
      { id: "T5.2", name: "Succulent or thorny deserts and semi-deserts" },
      { id: "T5.3", name: "Sclerophyll hot deserts and semi-deserts" },
      { id: "T5.4", name: "Cool deserts and semi-deserts" },
      { id: "T5.5", name: "Hyper-arid deserts" },
    ],
  },
  {
    id: "T6",
    name: "Polar-Alpine (Cryogenic)",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T6.1", name: "Ice sheets, glaciers and perennial snowfields" },
      { id: "T6.2", name: "Polar alpine rocky outcrops" },
      { id: "T6.3", name: "Polar tundra and deserts" },
      { id: "T6.4", name: "Temperate alpine grasslands and shrublands" },
      { id: "T6.5", name: "Tropical alpine grasslands and herbfields" },
    ],
  },
  {
    id: "T7",
    name: "Intensive Land-Use",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T7.1", name: "Annual croplands" },
      { id: "T7.2", name: "Sown pastures and fields" },
      { id: "T7.3", name: "Plantations" },
      { id: "T7.4", name: "Urban and industrial ecosystems" },
      { id: "T7.5", name: "Derived semi-natural pastures and old fields" },
    ],
  },
  {
    // Custom addition — not part of the official IUCN Global Ecosystem Typology.
    id: "T8",
    name: "Anthropogenic Terrestrial",
    realm: "Terrestrial",
    functionalGroups: [
      { id: "T8.1", name: "Home Garden" },
      { id: "T8.2", name: "Botanical Garden or Arboretum" },
      { id: "T8.3", name: "Anthropogenic Forest" },
    ],
  },
  // Non-terrestrial realms (Subterranean / Freshwater / Transitional) are temporarily
  // disabled — only the Terrestrial T-bands are offered for now. Re-enable when the
  // covenant/EcoScore rules for these realms are defined.
  // {
  //   id: "S1",
  //   name: "Subterranean Lithic",
  //   realm: "Subterranean",
  //   functionalGroups: [
  //     { id: "S1.1", name: "Aerobic caves" },
  //     { id: "S1.2", name: "Endolithic systems" },
  //   ],
  // },
  // {
  //   id: "S2",
  //   name: "Anthropogenic Subterranean Voids",
  //   realm: "Subterranean",
  //   functionalGroups: [{ id: "S2.1", name: "Anthropogenic subterranean voids" }],
  // },
  // {
  //   id: "F1",
  //   name: "Rivers and Streams",
  //   realm: "Freshwater",
  //   functionalGroups: [
  //     { id: "F1.1", name: "Permanent upland streams" },
  //     { id: "F1.2", name: "Permanent lowland rivers" },
  //     { id: "F1.3", name: "Freeze-thaw rivers and streams" },
  //     { id: "F1.4", name: "Seasonal upland streams" },
  //     { id: "F1.5", name: "Seasonal lowland rivers" },
  //     { id: "F1.6", name: "Episodic arid rivers" },
  //     { id: "F1.7", name: "Large lowland rivers" },
  //   ],
  // },
  // {
  //   id: "F2",
  //   name: "Lakes",
  //   realm: "Freshwater",
  //   functionalGroups: [
  //     { id: "F2.1", name: "Large permanent freshwater lakes" },
  //     { id: "F2.2", name: "Small permanent freshwater lakes" },
  //     { id: "F2.3", name: "Seasonal freshwater lakes" },
  //     { id: "F2.4", name: "Freeze-thaw freshwater lakes" },
  //     { id: "F2.5", name: "Ephemeral freshwater lakes" },
  //     { id: "F2.6", name: "Permanent salt and soda lakes" },
  //     { id: "F2.7", name: "Ephemeral salt lakes" },
  //     { id: "F2.8", name: "Artesian springs and oases" },
  //     { id: "F2.9", name: "Geothermal pools and wetlands" },
  //     { id: "F2.10", name: "Subglacial lakes" },
  //   ],
  // },
  // {
  //   id: "F3",
  //   name: "Artificial Wetlands",
  //   realm: "Freshwater",
  //   functionalGroups: [
  //     { id: "F3.1", name: "Large reservoirs" },
  //     { id: "F3.2", name: "Constructed lacustrine wetlands" },
  //     { id: "F3.3", name: "Rice paddies" },
  //     { id: "F3.4", name: "Freshwater aquafarms" },
  //     { id: "F3.5", name: "Canals, ditches and drains" },
  //   ],
  // },
  // {
  //   id: "TF1",
  //   name: "Palustrine Wetlands",
  //   realm: "Transitional (Terrestrial-Freshwater)",
  //   functionalGroups: [
  //     { id: "TF1.1", name: "Tropical flooded forests and peat forests" },
  //     { id: "TF1.2", name: "Subtropical-temperate forested wetlands" },
  //     { id: "TF1.3", name: "Permanent marshes" },
  //     { id: "TF1.4", name: "Seasonal floodplain marshes" },
  //     { id: "TF1.5", name: "Episodic arid floodplains" },
  //     { id: "TF1.6", name: "Boreal, temperate and montane peat bogs" },
  //     { id: "TF1.7", name: "Boreal and temperate fens" },
  //   ],
  // },
  // {
  //   id: "FM1",
  //   name: "Semi-Confined Transitional Waters",
  //   realm: "Transitional (Freshwater-Marine)",
  //   functionalGroups: [
  //     { id: "FM1.1", name: "Deepwater coastal inlets" },
  //     { id: "FM1.2", name: "Permanently open riverine estuaries and bays" },
  //     { id: "FM1.3", name: "Intermittently closed and open lakes and lagoons" },
  //   ],
  // },
  // {
  //   id: "MT1",
  //   name: "Shorelines",
  //   realm: "Transitional (Marine-Terrestrial)",
  //   functionalGroups: [
  //     { id: "MT1.1", name: "Rocky shorelines" },
  //     { id: "MT1.2", name: "Muddy shorelines" },
  //     { id: "MT1.3", name: "Sandy shorelines" },
  //     { id: "MT1.4", name: "Boulder and cobble shores" },
  //   ],
  // },
  // {
  //   id: "MT2",
  //   name: "Supralittoral Coastal",
  //   realm: "Transitional (Marine-Terrestrial)",
  //   functionalGroups: [{ id: "MT2.1", name: "Coastal shrublands and grasslands" }],
  // },
  // {
  //   id: "MT3",
  //   name: "Anthropogenic Shorelines",
  //   realm: "Transitional (Marine-Terrestrial)",
  //   functionalGroups: [{ id: "MT3.1", name: "Artificial shorelines" }],
  // },
  // {
  //   id: "MFT1",
  //   name: "Brackish Tidal",
  //   realm: "Transitional (Marine-Freshwater-Terrestrial)",
  //   functionalGroups: [
  //     { id: "MFT1.1", name: "Coastal river deltas" },
  //     { id: "MFT1.2", name: "Intertidal forests and shrublands" },
  //     { id: "MFT1.3", name: "Coastal saltmarshes and reedbeds" },
  //   ],
  // },
  // {
  //   id: "SF1",
  //   name: "Subterranean Freshwaters",
  //   realm: "Transitional (Subterranean-Freshwater)",
  //   functionalGroups: [
  //     { id: "SF1.1", name: "Underground streams and pools" },
  //     { id: "SF1.2", name: "Groundwater ecosystems" },
  //   ],
  // },
  // {
  //   id: "SF2",
  //   name: "Anthropogenic Subterranean Freshwaters",
  //   realm: "Transitional (Subterranean-Freshwater)",
  //   functionalGroups: [
  //     { id: "SF2.1", name: "Water pipes and subterranean canals" },
  //     { id: "SF2.2", name: "Flooded mines and other voids" },
  //   ],
  // },
];

export const ecoSystemsOptions = ecoSystemTypes.map((item) => {
  return {
    value: item,
    label: item,
  };
});
const provincesList = [
  { value: "Central Province", label: "Central Province" },
  { value: "Eastern Province", label: "Eastern Province" },
  { value: "Northern Province", label: "Northern Province" },
  { value: "Southern Province", label: "Southern Province" },
  { value: "Western Province", label: "Western Province" },
  { value: "North Western Province", label: "North Western Province" },
  { value: "North Central Province", label: "North Central Province" },
  { value: "Uva Province", label: "Uva Province" },
  { value: "Sabaragamuwa Province", label: "Sabaragamuwa Province" },
];

// Main type = IUCN Biome. Value is the biome code (e.g. "T1"); label shows the code
// alongside the name so trustees can cross-reference the IUCN typology.
export const ecoSystemMainTypes = IUCN_ECOSYSTEM_TYPOLOGY.map((biome) => {
  return {
    value: biome.id,
    label: `${biome.id} — ${biome.name}`,
  };
});

// Sub type = IUCN Functional Group within the selected biome. `type` is the biome
// code stored in ecoSystemMainType. Value is the functional-group code (e.g. "T1.1").
export const ecoSystemSubTypes = (type: string) => {
  return IUCN_ECOSYSTEM_TYPOLOGY.find((biome) => biome.id === type)?.functionalGroups.map((group) => {
    return {
      value: group.id,
      label: `${group.id} — ${group.name}`,
    };
  });
};

// Resolve a stored biome code back to its display name. Falls back to the raw value
// so lands saved under the legacy climate-zone labels still render their old text.
export const ecoSystemMainTypeName = (value?: string): string => {
  if (!value) {
    return "";
  }
  return IUCN_ECOSYSTEM_TYPOLOGY.find((biome) => biome.id === value)?.name ?? value;
};

// Resolve a stored functional-group code back to its display name. Falls back to the
// raw value so legacy lands (which stored the ecosystem name, not a code) still render.
export const ecoSystemSubTypeName = (value?: string): string => {
  if (!value) {
    return "";
  }
  for (const biome of IUCN_ECOSYSTEM_TYPOLOGY) {
    const group = biome.functionalGroups.find((g) => g.id === value);
    if (group) {
      return group.name;
    }
  }
  return value;
};

const easementsBlockDescription =
  // eslint-disable-next-line max-len
  "You can specify special easements related to the land such as private access, accommodation, goods or production that you are willing to provide a ESG investor, and that will be unlocked by a minimum activation bid.";
const startBidDesc =
  // eslint-disable-next-line max-len
  "Starting bid, from which auction will begin, users will not be able to bid below this price. Also this value can be considered as required amount to complete Basic Forest Development Plan";

export { ecoSystemTypes, provincesList, easementsBlockDescription, startBidDesc };
